import type { ItemList, VideoGallery, WithContext } from 'schema-dts';

import { config } from './config';
import type {
    BaseVideoItem,
    InteractionStatisticsResult,
    VideoContainer,
    VideoFormat,
    VideoSchemaGeneratorProps,
} from './types/video';
import { DurationCache, fail, isOk, LRUCache, ok, Result, safeNumber, safeString, toAbsoluteUrl } from './utils';
import { logger } from './utils/logger';
import { validateVideoItem } from './utils/validation';

// Initialize caches with configuration
const videoObjectCache = new LRUCache<string, unknown>({
    maxEntries: config.getConfig().cache.maxVideoEntries,
    ttl: config.getConfig().cache.ttl,
});

const durationCache = new DurationCache();

/**
 * Generate video format information
 */
const getVideoFormat = (stockItem: BaseVideoItem): VideoFormat => {
    const format: VideoFormat = {
        encodingFormat: 'video/mp4',
    };

    if (stockItem.maxResolution) {
        const resolution = safeNumber(stockItem.maxResolution);
        format.videoQuality = `${resolution}p`;
        format.height = `${resolution}px`;

        if (stockItem.aspectRatio) {
            const ratio = safeNumber(stockItem.aspectRatio, 16 / 9);
            format.width = `${Math.round(resolution * ratio)}px`;
        } else {
            format.width = `${Math.round(resolution * (16 / 9))}px`;
        }
    }

    if (stockItem.fileSize) {
        format.contentSize = `${safeNumber(stockItem.fileSize)}MB`;
    }

    if (stockItem.bitrate) {
        format.bitrate = `${safeNumber(stockItem.bitrate)}kbps`;
    }

    return format;
};

/**
 * Generate pricing and licensing information
 */
const getPricingInfo = (stockItem: BaseVideoItem): Record<string, unknown> => {
    const cfg = config.getConfig();
    const pricing: Record<string, unknown> = {
        license: config.buildUrl(cfg.storyblocks.endpoints.pricing),
        acquireLicensePage: stockItem.detailsUrl ? toAbsoluteUrl(stockItem.detailsUrl) : undefined,
        isAccessibleForFree: false,
        offers: {
            '@type': 'Offer',
            category: 'Subscription',
            businessFunction: {
                '@type': 'BusinessFunction',
                '@id': 'http://purl.org/goodrelations/v1#LeaseOut',
            },
            seller: config.getOrganizationSchema(),
            url: config.buildUrl(cfg.storyblocks.endpoints.pricing),
            description: 'Individual license, higher quality stock content and more.',
        },
    };

    return pricing;
};

/**
 * Generate interaction statistics
 */
const getInteractionStatistics = (stockItem: BaseVideoItem): InteractionStatisticsResult => {
    const stats: InteractionStatisticsResult = {};

    if (stockItem.viewCount) {
        const watchInteraction = {
            '@type': 'InteractionCounter' as const,
            '@id': 'https://schema.org/WatchAction',
            interactionType: {
                '@type': 'Action',
                '@id': 'https://schema.org/WatchAction',
            },
            userInteractionCount: safeNumber(stockItem.viewCount),
        };
        stats.interactionStatistic = [watchInteraction];
    }

    if (stockItem.downloadCount) {
        const downloadInteraction = {
            '@type': 'InteractionCounter' as const,
            '@id': 'https://schema.org/DownloadAction',
            interactionType: {
                '@type': 'Action',
                '@id': 'https://schema.org/DownloadAction',
            },
            userInteractionCount: safeNumber(stockItem.downloadCount),
        };

        if (!stats.interactionStatistic) {
            stats.interactionStatistic = [downloadInteraction];
        } else if (Array.isArray(stats.interactionStatistic)) {
            stats.interactionStatistic.push(downloadInteraction);
        } else {
            stats.interactionStatistic = [stats.interactionStatistic, downloadInteraction];
        }
    }

    const cfg = config.getConfig().validation;
    if (stockItem.rating && stockItem.ratingCount) {
        const rating = {
            '@type': 'AggregateRating' as const,
            ratingValue: safeNumber(stockItem.rating),
            ratingCount: safeNumber(stockItem.ratingCount),
            bestRating: cfg.maxRating,
            worstRating: cfg.minRating,
        };
        stats.aggregateRating = rating;
    }

    return stats;
};

/**
 * VideoSchemaBuilder - Cleaner approach to building video objects
 */
class VideoSchemaBuilder {
    private videoObject: Record<string, unknown> = {};
    private config = config.getConfig();

    constructor(
        private videoItem: BaseVideoItem,
        private index: number,
    ) {
        this.initializeBasicInfo();
    }

    private initializeBasicInfo(): this {
        const fallbackTitle = `Video ${this.index + 1}`;
        this.videoObject = {
            '@type': 'VideoObject',
            '@id': `#video-${this.videoItem.id || this.index}`,
            name: safeString(this.videoItem.title, fallbackTitle),
            description: safeString(
                this.videoItem.description,
                `${this.videoItem.title || fallbackTitle} - Royalty-free stock video from Storyblocks`,
            ),
            thumbnailUrl: this.videoItem.thumbnailUrl || this.videoItem.mediumImageUrl || this.videoItem.largeImageUrl,
            uploadDate: this.videoItem.dateAdded || new Date().toISOString(),
            url: this.videoItem.detailsUrl ? toAbsoluteUrl(this.videoItem.detailsUrl) : undefined,
            embedUrl: this.videoItem.previewUrl,
            contentUrl: this.videoItem.previewUrl,
            publisher: config.getOrganizationSchema(),
        };
        return this;
    }

    addDuration(): this {
        if (this.videoItem.duration) {
            this.videoObject.duration = durationCache.convertToIsoDuration(safeNumber(this.videoItem.duration));
        }
        return this;
    }

    addCategories(): this {
        if (this.videoItem.categories?.length) {
            const categoryNames = this.videoItem.categories.map((cat: { name: string }) => cat.name);
            this.videoObject.keywords = categoryNames.join(', ');
            this.videoObject.genre = categoryNames;
            this.videoObject.about = {
                '@type': 'Thing',
                name: categoryNames.join(', '),
                url: config.buildUrl(`/video/search/${encodeURIComponent(categoryNames.join(', '))}`),
            };
        }
        return this;
    }

    addContributor(): this {
        if (this.videoItem.contributor?.username) {
            this.videoObject.creator = {
                '@type': 'Person',
                name: this.videoItem.contributor.username,
                url: config.buildUrl(`/video/portfolio/${this.videoItem.contributor.username}`),
                ...(this.videoItem.contributor.profileImageUrl && {
                    image: this.videoItem.contributor.profileImageUrl,
                }),
            };
        }
        return this;
    }

    addFormatInfo(): this {
        Object.assign(this.videoObject, getVideoFormat(this.videoItem));
        return this;
    }

    addPricingInfo(): this {
        Object.assign(this.videoObject, getPricingInfo(this.videoItem));
        return this;
    }

    addInteractionStats(): this {
        Object.assign(this.videoObject, getInteractionStatistics(this.videoItem));
        return this;
    }

    addMetadata(): this {
        if (this.videoItem.tags?.length) {
            this.videoObject.contentTags = this.videoItem.tags;
        }

        if (this.videoItem.language) {
            this.videoObject.inLanguage = this.videoItem.language;
        }

        this.videoObject.potentialAction = [
            {
                '@type': 'WatchAction',
                target: this.videoItem.previewUrl,
                name: 'Preview Video',
            },
            {
                '@type': 'DownloadAction',
                target: this.videoItem.detailsUrl ? toAbsoluteUrl(this.videoItem.detailsUrl) : undefined,
                name: 'Download Video',
            },
            {
                '@type': 'ShareAction',
                target: this.videoItem.detailsUrl ? toAbsoluteUrl(this.videoItem.detailsUrl) : undefined,
                name: 'Share Video',
            },
        ].filter((action) => action.target);

        if (this.videoItem.hasClosedCaptions) {
            this.videoObject.accessibilityFeature = ['closedCaptions'];
        }

        return this;
    }

    build(): Record<string, unknown> {
        return this.videoObject;
    }
}

/**
 * Enhanced video object generation using builder pattern
 */
const createVideoObject = <T extends BaseVideoItem>(videoItem: T, index: number): Result<Record<string, unknown>> => {
    // Validate input
    const validationResult = validateVideoItem(videoItem);
    if (!isOk(validationResult)) {
        logger.warn('Invalid video item', { errors: validationResult.error });
        return fail(new Error('Invalid video item'));
    }

    const dateModified = videoItem.dateModified || videoItem.dateAdded;
    const cacheKey = `${videoItem.id}-${dateModified}`;

    // Check cache
    const cached = videoObjectCache.get(cacheKey);
    if (cached) {
        return ok(cached as Record<string, unknown>);
    }

    // Build new video object
    const builder = new VideoSchemaBuilder(videoItem, index);
    const videoObject = builder
        .addDuration()
        .addCategories()
        .addContributor()
        .addFormatInfo()
        .addPricingInfo()
        .addInteractionStats()
        .addMetadata()
        .build();

    // Cache the result
    videoObjectCache.set(cacheKey, videoObject);

    return ok(videoObject);
};

/**
 * Generates optimized JSON-LD schema for video collections
 * Enhanced for SEO with comprehensive schema.org compliance
 */
export const generateVideoCollectionSchema = <T extends VideoContainer<BaseVideoItem>>({
    heading,
    description,
    videos,
    pageUrl,
    contentMode,
    category,
    searchTerm,
    totalResults,
    currentPage,
    itemsPerPage,
}: VideoSchemaGeneratorProps<T>): string => {
    if (!videos?.length) {
        logger.debug('No videos provided for schema generation');
        return '';
    }

    const validVideos = videos.filter((video: T) => video?.stockItem?.id);
    if (validVideos.length === 0) {
        logger.warn('No valid videos found for schema generation');
        return '';
    }

    const generateDescription = (): string => {
        if (description) return description;

        const count = totalResults || videos.length;
        const baseDescription = `Browse ${count.toLocaleString()} royalty-free video${count === 1 ? '' : 's'}`;

        if (searchTerm) {
            return `${baseDescription} for "${searchTerm}" on Storyblocks. High-quality stock footage for your creative projects.`;
        }

        if (category) {
            return `${baseDescription} in the ${category} category on Storyblocks. Professional stock video content.`;
        }

        return `${baseDescription} on Storyblocks. Premium stock video library for creators.`;
    };

    // Build video objects with error handling
    const videoObjects: Record<string, unknown>[] = [];
    validVideos.forEach((video: T, index: number) => {
        const result = createVideoObject(video.stockItem, index);
        if (isOk(result)) {
            videoObjects.push(result.data);
        } else {
            logger.warn(`Failed to create video object for index ${index}`, {
                videoId: video.stockItem.id,
            });
        }
    });

    if (videoObjects.length === 0) {
        logger.error('Failed to create any valid video objects');
        return '';
    }

    const cfg = config.getConfig();
    const schema = {
        '@context': 'https://schema.org',
        '@type': contentMode === 'collection' ? 'VideoGallery' : 'ItemList',
        name: heading,
        description: generateDescription(),
        numberOfItems: totalResults || validVideos.length,
        publisher: config.getOrganizationSchema(),
        itemListElement: videoObjects.map((video: Record<string, unknown>, index: number) => ({
            '@type': 'ListItem',
            position: (currentPage ? (currentPage - 1) * (itemsPerPage || validVideos.length) : 0) + index + 1,
            item: video,
        })),
    } as WithContext<ItemList | VideoGallery>;

    if (pageUrl) {
        schema.url = pageUrl;
    }

    // Add pagination info
    if (currentPage && itemsPerPage && totalResults) {
        const totalPages = Math.ceil(totalResults / itemsPerPage);
        if (totalPages > 1) {
            (schema as unknown as Record<string, unknown>).mainEntity = {
                '@type': 'CollectionPage',
                isPartOf: {
                    '@type': 'ItemList',
                    numberOfItems: totalResults,
                },
            };
        }
    }

    // Add search/category context
    if (contentMode === 'search' && (category || searchTerm)) {
        (schema as unknown as Record<string, unknown>).about = {
            '@type': 'Thing',
            name: searchTerm || category,
            description: `Professional video content related to ${searchTerm || category}`,
            sameAs: category ? config.buildUrl(`/video/search/${encodeURIComponent(category)}`) : undefined,
        };
    }

    // Add breadcrumb
    if (category) {
        (schema as unknown as Record<string, unknown>).breadcrumb = {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: cfg.storyblocks.baseUrl,
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Videos',
                    item: config.buildUrl(cfg.storyblocks.endpoints.video),
                },
                {
                    '@type': 'ListItem',
                    position: 3,
                    name: category,
                    item: config.buildUrl(`/video/search/${encodeURIComponent(category)}`),
                },
            ],
        };
    }

    // Add website reference
    (schema as unknown as Record<string, unknown>).isPartOf = config.getWebsiteSchema();

    logger.info('Generated video collection schema', {
        videoCount: videoObjects.length,
        contentMode,
        hasCategory: !!category,
        hasSearchTerm: !!searchTerm,
    });

    return JSON.stringify(schema, null, 0);
};
